import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { getOverviewSidebarVisible } from '../../Reducers/UI/Selectors';
import {
  getCalculationSelectedOptions,
  getDeliveryTime,
  getTotalFormatted,
  getHasVisitedAllSteps,
} from '../../Reducers/Configurator/Selectors';
import { showConfigurationOverView } from '../../Reducers/UI/Actions';
import { liightergrey, borderColor, boxShadowColor, grey, green } from '../../Constants/Colors';
import iconArrowRight from '../../Resources/Images/icon-arrow-right-1.svg';
import iconArrowRight2 from '../../Resources/Images/icon-arrow-right-2.svg';
import { toggleOverviewSidebar } from '../../Reducers/UI/Actions';
import { Drawer } from '@material-ui/core';
import { Toolbar } from '../Toolbar/Toolbar';
import Typography from '@material-ui/core/Typography';
import { Option } from '../../types';
import { hidePrices } from '../../Helpers';
import clsx from 'clsx';
import PriceInfoDialog from '../PriceInfoDialog/PriceInfoDialog';
import { useMediaQuery, useTheme } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';

const useStyles = makeStyles(
  (theme) => ({
    OverviewSidebarContainer: {
      minWidth: '375px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: 'solid 2px ' + borderColor,
      borderLeft: '0px',
      overflow: 'hidden',
    },
    header: {
      height: '70px',
      width: '100%',
      display: 'flex',
      padding: '0 20px 0 20px',
      flexDirection: 'row',
      backgroundColor: liightergrey,
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    headerTitleContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    headerTitle: {
      fontFamily: 'Inter',
    },
    iconArrow: {
      width: '26px',
      height: '18px',
      marginRight: '20px',
    },
    iconArrow2: {
      width: '7px',
      height: '14px',
    },
    optionsList: {
      margin: '20px 20px 20px 20px',
      overflow: 'auto',
      flexDirection: 'column',
      maxHeight: '80%',
      '&::-webkit-scrollbar': {
        width: '0.5em',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: borderColor,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'black',
      },
    },
    btnFinish: {
      width: '100%',
      height: '50px',
      alignSelf: 'flex-end',
      fontSize: 14,
      fontFamily: 'Inter',
      fontWeight: 700,
      [theme.breakpoints.down('xs')]: {
        minWidth: 150,
      },
    },
    optionDetails: {
      padding: '20px 0 20px 0',
      marginRight: '20px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      flexGrow: 1,
      borderBottom: 'solid 1px ' + grey,
    },
    lastOptionDetails: {
      borderBottom: 'none',
    },
    optionTitlePrice: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: '16px',
      [theme.breakpoints.up('sm')]: {
        gap: '24px',
      },
    },
    optionTitlePriceLabel: {
      fontFamily: 'Inter',
      fontWeight: 500,
    },
    optionPriceLabel: {
      textAlign: 'end',
    },
    optionAbstract: {
      fontFamily: 'Inter',
      fontWeight: 200,
    },
    footer: {
      height: '167px',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '0 20px 20px 20px',
      marginTop: 'auto',
      boxShadow: '0px -3px 6px ' + boxShadowColor,
    },
    deliveryDetails: {
      padding: '20px 20px 20px 0',
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    deliveryDetailLabels: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    configurationPriceLabel: {
      fontFamily: 'Inter',
      fontWeight: 500,
    },
    deliveryDetailValues: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    deliveryTimeLabel: {
      fontFamily: 'Inter',
      fontWeight: 300,
      color: green,
    },
    clickable: {
      cursor: 'pointer',
    },
  }),
  {
    name: 'OverviewSidebar',
  }
);

const OverviewSidebar = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const visible = useSelector(getOverviewSidebarVisible);
  const selectedOptions: Array<Option> = useSelector(getCalculationSelectedOptions);
  const deliveryTime = useSelector(getDeliveryTime);
  const totalFormatted = useSelector(getTotalFormatted);
  const hasVisitedAllSteps = useSelector(getHasVisitedAllSteps);
  const pricesHidden = hidePrices();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showPriceInfo, setShowPriceInfo] = useState(false);
  const onPriceClick = () => {
    setShowPriceInfo(true);
  };

  const onCancelPriceInfo = () => {
    setShowPriceInfo(false);
  };
  const finishConfiguration = () => {
    dispatch(showConfigurationOverView());
    dispatch(toggleOverviewSidebar());
  };

  const parseDeliveryTime = () => {
    if (deliveryTime.min !== deliveryTime.max) {
      return `${deliveryTime.min} - ${deliveryTime.max}`;
    }
    return deliveryTime.min;
  };

  return (
    <Drawer anchor={'right'} open={visible} onClose={() => dispatch(toggleOverviewSidebar())}>
      <PriceInfoDialog onCancel={onCancelPriceInfo} open={showPriceInfo} />
      <div className={classes.OverviewSidebarContainer}>
        <div className={classes.header}>
          <div className={classes.headerTitleContainer}>
            {!isMobile && <img className={classes.iconArrow} src={iconArrowRight} />}
            {isMobile && (
              <Icon
                className={classes.iconArrow}
                src={iconArrowRight}
                onClick={() => dispatch(toggleOverviewSidebar())}
              >
                close
              </Icon>
            )}
            <Typography variant="h6" className={classes.headerTitle}>
              {t('general.yourConfiguration')}
            </Typography>
          </div>

          <Toolbar highlightSave={false} loadEnabled={false} showLabels={false} />
        </div>
        <div className={classes.optionsList}>
          {selectedOptions.map((option, i) => (
            <div
              key={i}
              className={`${classes.optionDetails} ${
                i === selectedOptions.length - 1 ? classes.lastOptionDetails : ''
              }`}
            >
              <div className={classes.optionTitlePrice}>
                <Typography variant="body1" className={classes.optionTitlePriceLabel}>
                  {option.title}
                </Typography>
                {!pricesHidden && (
                  <Typography
                    variant="body1"
                    onClick={option.price !== 0 ? onPriceClick : null}
                    className={clsx(
                      classes.optionTitlePriceLabel,
                      classes.optionPriceLabel,
                      option.price !== 0 ? classes.clickable : ''
                    )}
                  >
                    {option.price === 0 ? t('optionTile.noPrice') : option.priceFormatted + '*'}
                  </Typography>
                )}
              </div>
              <div>
                <Typography variant="body1" className={classes.optionAbstract}>
                  {option.abstract}
                </Typography>
              </div>
              {option.componentTitle && (
                <div>
                  <Typography variant="body1" className={classes.optionAbstract}>
                    {option.componentTitle}
                  </Typography>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className={classes.footer}>
          <div className={classes.deliveryDetails}>
            <div className={classes.deliveryDetailLabels}>
              {!pricesHidden && (
                <Typography variant="body1" className={classes.configurationPriceLabel}>
                  {t('overviewSidebar.total')}
                </Typography>
              )}
              <Typography variant="body1" className={classes.deliveryTimeLabel}>
                {t('overviewSidebar.deliveryTime')}
              </Typography>
            </div>
            <div className={classes.deliveryDetailValues}>
              {!pricesHidden && (
                <Typography
                  onClick={onPriceClick}
                  variant="body1"
                  className={clsx(classes.configurationPriceLabel, classes.clickable)}
                >
                  {totalFormatted + '*'}
                </Typography>
              )}
              <Typography variant="body1" className={classes.deliveryTimeLabel}>
                {deliveryTime
                  ? `${parseDeliveryTime()} ${t('overviewSidebar.weeks')}`
                  : t('overviewSidebar.unknown')}
              </Typography>
            </div>
          </div>
          <Typography variant="body1">{'* ' + t('pdf.netHint')}</Typography>
          <Button
            className={classes.btnFinish}
            disabled={!hasVisitedAllSteps}
            variant="contained"
            endIcon={<img className={classes.iconArrow2} src={iconArrowRight2} />}
            onClick={finishConfiguration}
          >
            {t('overviewSidebar.finishConfiguration')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default OverviewSidebar;
